import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { CameraCapture } from '@/components/CameraCapture';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { MapPin, Send, Loader2, Navigation, Construction } from 'lucide-react';
import { toast } from 'sonner';

const BLACKSPOT_TYPES = [
  'Potholes', 'Broken Traffic Signals', 'Missing Road Signs',
  'Accident-Prone Intersection', 'Poor Street Lighting', 'Waterlogging',
  'Road Damage', 'Unmarked Speed Breaker', 'Narrow Road', 'Other'
];

export default function ReportBlackspotPage() {
  const { authAxios, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [media, setMedia] = useState(null);
  const [location, setLocation] = useState(null);
  const [locLoading, setLocLoading] = useState(false);
  const [locError, setLocError] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [legalConfirmed, setLegalConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);

  const getLocation = useCallback(() => {
    setLocLoading(true);
    setLocError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          altitude: pos.coords.altitude,
          accuracy: pos.coords.accuracy,
          speed: pos.coords.speed,
        });
        setLocLoading(false);
        setStep(3);
      },
      () => {
        setLocError('Location access is required. Please enable GPS.');
        setLocLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const handleCapture = (captureData) => {
    setMedia(captureData);
    if (captureData) setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!media) { toast.error('Please capture evidence'); return; }
    if (!location) { toast.error('Location is required'); return; }
    if (!category) { toast.error('Select issue category'); return; }
    if (!legalConfirmed) { toast.error('Confirm the legal notice'); return; }

    setSubmitting(true);
    try {
      const ua = navigator.userAgent;
      await authAxios.post('/reports', {
        report_type: 'blackspot',
        category,
        description,
        vehicle_number: '',
        latitude: location.latitude,
        longitude: location.longitude,
        altitude: location.altitude,
        gps_accuracy: location.accuracy,
        speed: location.speed,
        device_type: /mobile/i.test(ua) ? 'mobile' : 'desktop',
        browser: ua.substring(0, 100),
        anonymous,
        legal_confirmed: legalConfirmed,
        media_data: media.data,
        media_type: media.type,
      });
      toast.success('Blackspot report submitted!');
      navigate('/my-reports');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
    setSubmitting(false);
  };

  if (authLoading) return <div className="flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!user) { navigate('/login'); return null; }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8" data-testid="report-blackspot-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--warning))]/10 flex items-center justify-center">
            <Construction className="w-5 h-5 text-[hsl(var(--warning))]" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Report Road Blackspot</h1>
            <p className="text-sm text-muted-foreground">Report road safety hazards for repair</p>
          </div>
        </div>
        <div className="flex gap-2 mt-6">
          {['Capture', 'Location', 'Details'].map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full transition-colors ${step > i ? 'bg-[hsl(var(--warning))]' : step === i + 1 ? 'bg-[hsl(var(--warning))]/50' : 'bg-muted'}`} />
              <span className="text-xs text-muted-foreground mt-1 block">{s}</span>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card data-testid="blackspot-step-capture">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[hsl(var(--warning))] text-black text-xs flex items-center justify-center font-bold">1</span>
              Capture Evidence
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CameraCapture onCapture={handleCapture} mode="photo" />
          </CardContent>
        </Card>

        {step >= 2 && (
          <Card className="animate-fade-in-up" data-testid="blackspot-step-location">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[hsl(var(--warning))] text-black text-xs flex items-center justify-center font-bold">2</span>
                Verify Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!location ? (
                <div className="text-center py-6 space-y-4">
                  <MapPin className="w-10 h-10 text-muted-foreground mx-auto" />
                  <p className="text-sm text-muted-foreground">Location access is required for accurate reporting.</p>
                  {locError && <p className="text-sm text-destructive">{locError}</p>}
                  <Button type="button" onClick={getLocation} disabled={locLoading} className="gap-2" data-testid="blackspot-get-location-btn">
                    {locLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                    {locLoading ? 'Getting Location...' : 'Enable Location'}
                  </Button>
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-muted">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground block text-xs">Latitude</span>
                      <span className="font-mono font-medium">{location.latitude.toFixed(6)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block text-xs">Longitude</span>
                      <span className="font-mono font-medium">{location.longitude.toFixed(6)}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {step >= 3 && (
          <Card className="animate-fade-in-up" data-testid="blackspot-step-details">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[hsl(var(--warning))] text-black text-xs flex items-center justify-center font-bold">3</span>
                Issue Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Issue Category *</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="blackspot-category-select">
                    <SelectValue placeholder="Select issue category" />
                  </SelectTrigger>
                  <SelectContent>
                    {BLACKSPOT_TYPES.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Describe the road issue..."
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={3}
                  data-testid="blackspot-description-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox id="bs-anon" checked={anonymous} onCheckedChange={setAnonymous} data-testid="blackspot-anonymous-checkbox" />
                <Label htmlFor="bs-anon" className="text-sm cursor-pointer">Submit anonymously</Label>
              </div>

              <div className="p-4 rounded-lg border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5">
                <p className="text-sm font-medium mb-3">
                  Submitting false reports may lead to account suspension and legal action.
                </p>
                <div className="flex items-center gap-2">
                  <Checkbox id="bs-legal" checked={legalConfirmed} onCheckedChange={setLegalConfirmed} data-testid="blackspot-legal-checkbox" />
                  <Label htmlFor="bs-legal" className="text-sm cursor-pointer">I confirm this report is genuine</Label>
                </div>
              </div>

              <Button type="submit" className="w-full font-bold uppercase tracking-wide gap-2" disabled={submitting} size="lg" data-testid="submit-blackspot-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? 'Submitting...' : 'Submit Report'}
              </Button>
            </CardContent>
          </Card>
        )}
      </form>
    </div>
  );
}
